import {ReactNode, useEffect, useRef, useState} from 'react';
import Analytics from 'utils/Analytics';
import {RiseHeader} from 'screens/InsomniaSurvey/components/RiseHeader/RiseHeader';
import {OsaShell} from 'surveys/osa_funnel/components/OsaShell';
import {readSeverityFromUrl} from 'surveys/osa_funnel/survey/severity';
import {getRiseAppDownloadUrl, isAndroid} from 'surveys/osa_funnel/survey/appDownload';
import {useGlp1Modality} from 'surveys/osa_funnel/hooks/useGlp1Modality';
import {modalityToVariant} from '../paywall/paywallCopy';
import {Glp1SoldOutModal} from './Glp1SoldOutModal';
import styles from './Glp1MedicalScreening.module.css';

const CheckGlyph = () => (
  <svg width={14} height={14} viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth={2.4}>
    <path d="M3 7.5 L 6 10.5 L 11 4.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

type ConditionValue =
  | 'medullary_thyroid_carcinoma'
  | 'multiple_endocrine_neoplasia'
  | 'pancreatitis'
  | 'severe_gi_disease'
  | 'pregnancy'
  | 'gallbladder_disease'
  | 'none_of_these';

const CONDITIONS: {value: ConditionValue; label: string}[] = [
  {value: 'medullary_thyroid_carcinoma', label: 'Medullary thyroid carcinoma (MTC) or family history'},
  {value: 'multiple_endocrine_neoplasia', label: 'Multiple endocrine neoplasia syndrome type 2 (MEN-2)'},
  {value: 'pancreatitis', label: 'Pancreatitis (any history)'},
  {value: 'severe_gi_disease', label: 'Severe gastrointestinal disease (gastroparesis, etc.)'},
  {value: 'pregnancy', label: 'Currently pregnant, breastfeeding, or planning pregnancy'},
  {value: 'gallbladder_disease', label: 'Gallbladder disease (any history)'},
  {value: 'none_of_these', label: 'None of these apply to me'},
];

const TOTAL_STEPS = 3;

type YesNo = 'yes' | 'no' | null;

/** GLP-1 medical screening — 3-step form. Shown after the paywall
 *  finalize call succeeds. On final submit, opens the sold-out modal
 *  (fake-door overlay) → "Back to RISE" exits to onboarding. */
export const Glp1MedicalScreening = () => {
  const modality = useGlp1Modality();
  const variant = modalityToVariant(modality);

  const [step, setStep] = useState(1);
  const [conditions, setConditions] = useState<Set<ConditionValue>>(new Set());
  const [medsAnswer, setMedsAnswer] = useState<YesNo>(null);
  const [medsText, setMedsText] = useState('');
  const [allergiesAnswer, setAllergiesAnswer] = useState<YesNo>(null);
  const [allergiesText, setAllergiesText] = useState('');
  const [showSoldOut, setShowSoldOut] = useState(false);
  // The scroll body: on desktop the card is overflow:hidden and THIS element
  // is the scroller (window doesn't move); on mobile the window scrolls.
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const eventName =
      step === 1
        ? 'GLP-1 Funnel Medical Conditions Viewed'
        : step === 2
          ? 'GLP-1 Funnel Medical Medications Viewed'
          : 'GLP-1 Funnel Medical Allergies Viewed';
    Analytics.track(eventName, {variant, severity: readSeverityFromUrl()});
    // Snap to top so each step lands with the header in view — reset both
    // the inner scroll container (desktop) and the window (mobile).
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({top: 0, behavior: 'auto'});
      window.scrollTo({top: 0, behavior: 'auto'});
    });
  }, [step, variant]);

  const toggleCondition = (value: ConditionValue) => {
    setConditions(prev => {
      const next = new Set(prev);
      // "None of these apply" is mutually exclusive — picking it clears
      // the others; picking any other clears "none of these".
      if (value === 'none_of_these') {
        if (next.has('none_of_these')) {
          next.delete('none_of_these');
        } else {
          next.clear();
          next.add('none_of_these');
        }
        return next;
      }
      next.delete('none_of_these');
      if (next.has(value)) next.delete(value);
      else next.add(value);
      return next;
    });
  };

  const step1Valid = conditions.size > 0;
  const step2Valid = medsAnswer !== null && (medsAnswer === 'no' || medsText.trim().length > 0);
  const step3Valid = allergiesAnswer !== null && (allergiesAnswer === 'no' || allergiesText.trim().length > 0);

  const canContinue = step === 1 ? step1Valid : step === 2 ? step2Valid : step3Valid;

  const handleContinue = () => {
    if (!canContinue) return;
    if (step === 1) {
      Analytics.track('GLP-1 Funnel Medical Conditions Submitted', {
        values: Array.from(conditions),
        variant,
      });
      setStep(2);
    } else if (step === 2) {
      Analytics.track('GLP-1 Funnel Medical Medications Submitted', {
        answer: medsAnswer,
        // Free-text answers shouldn't ship to general analytics destinations
        // in production — confirm scrubbing / BAA destination before this
        // surface goes outside the fake-door cohort.
        hasText: medsText.trim().length > 0,
        variant,
      });
      setStep(3);
    } else {
      Analytics.track('GLP-1 Funnel Medical Allergies Submitted', {
        answer: allergiesAnswer,
        hasText: allergiesText.trim().length > 0,
        variant,
      });
      Analytics.track('GLP-1 Funnel Medical Screening Completed', {variant});
      setShowSoldOut(true);
    }
  };

  const handleSoldOutContinue = () => {
    // Terminal hand-off — send the user straight to the app store
    // (Android → Play, else App Store) to claim the "two months of RISE,
    // on us" offer, instead of bouncing back to onboarding (which could
    // re-flag them right into this funnel).
    const url = getRiseAppDownloadUrl();
    Analytics.track('GLP-1 Funnel Download App Tapped', {
      variant,
      severity: readSeverityFromUrl(),
      destination_url: url,
      platform: isAndroid() ? 'android' : 'ios',
    });
    window.location.assign(url);
  };

  return (
    <>
      <OsaShell
        ref={scrollRef}
        header={
          <>
            <RiseHeader
              backButton={step > 1}
              onBackClick={() => setStep(s => s - 1)}
              enableCategoryHeader
              category="Medical history"
            />
            {/* Spacing comes from osaShell.css `.osa-progress-wrap` (!important). */}
            <div className="osa-progress-wrap">
              <div className={styles.stepBar} aria-hidden="true">
                <div className={styles.stepBarFill} style={{width: `${(step / TOTAL_STEPS) * 100}%`}} />
              </div>
            </div>
          </>
        }
        footer={
          <div className={styles.ctaBar}>
            <div className={styles.ctaInner}>
              <button type="button" className={styles.cta} onClick={handleContinue} disabled={!canContinue}>
                {step < TOTAL_STEPS ? 'Save & Continue' : 'Submit'}
              </button>
            </div>
          </div>
        }
      >
        <div className={styles.content}>
          {step === 1 ? (
            <Step1Conditions selected={conditions} onToggle={toggleCondition} />
          ) : step === 2 ? (
            <Step2YesNoText
              headline={
                <>
                  Do you take any <strong>medications or supplements</strong>?
                </>
              }
              body="This helps your clinician get the full picture."
              answer={medsAnswer}
              onAnswerChange={setMedsAnswer}
              textValue={medsText}
              onTextChange={setMedsText}
              textLabel="What are you taking?"
              textPlaceholder="e.g. Lisinopril 10mg daily, Vitamin D3, Magnesium glycinate at bedtime"
            />
          ) : (
            <Step2YesNoText
              headline={
                <>
                  Do you have any <strong>drug allergies</strong>?
                </>
              }
              body="This helps your clinician get the full picture."
              answer={allergiesAnswer}
              onAnswerChange={setAllergiesAnswer}
              textValue={allergiesText}
              onTextChange={setAllergiesText}
              textLabel="What are you allergic to?"
              textPlaceholder="e.g. Penicillin"
            />
          )}
        </div>
      </OsaShell>

      <Glp1SoldOutModal isVisible={showSoldOut} onContinue={handleSoldOutContinue} />
    </>
  );
};

const Step1Conditions = ({
  selected,
  onToggle,
}: {
  selected: Set<ConditionValue>;
  onToggle: (v: ConditionValue) => void;
}) => (
  <>
    <h1 className={styles.headline}>Have you, or any close family, ever had any of these?</h1>
    <p className={styles.body}>
      Some conditions mean GLP-1 medication won&apos;t be safe, or need a closer look. Honest answers protect you.
    </p>
    <div className={styles.optionList}>
      {CONDITIONS.map(c => {
        const isSelected = selected.has(c.value);
        return (
          <button
            key={c.value}
            type="button"
            className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
            onClick={() => onToggle(c.value)}
            aria-pressed={isSelected}
          >
            <span className={styles.optionLabel}>{c.label}</span>
            <span className={styles.check} aria-hidden="true">
              {isSelected ? <CheckGlyph /> : null}
            </span>
          </button>
        );
      })}
    </div>
  </>
);

const Step2YesNoText = ({
  headline,
  body,
  answer,
  onAnswerChange,
  textValue,
  onTextChange,
  textLabel,
  textPlaceholder,
}: {
  headline: ReactNode;
  body: string;
  answer: YesNo;
  onAnswerChange: (v: YesNo) => void;
  textValue: string;
  onTextChange: (v: string) => void;
  textLabel: string;
  textPlaceholder: string;
}) => (
  <>
    <h1 className={styles.headline}>{headline}</h1>
    <p className={styles.body}>{body}</p>
    <div className={styles.yesNoRow}>
      <button
        type="button"
        className={`${styles.yesNoButton} ${answer === 'yes' ? styles.yesNoButtonActive : ''}`}
        onClick={() => onAnswerChange('yes')}
        aria-pressed={answer === 'yes'}
      >
        Yes
      </button>
      <button
        type="button"
        className={`${styles.yesNoButton} ${answer === 'no' ? styles.yesNoButtonActive : ''}`}
        onClick={() => onAnswerChange('no')}
        aria-pressed={answer === 'no'}
      >
        No
      </button>
    </div>
    {answer === 'yes' ? (
      <div className={styles.textField}>
        <label className={styles.textFieldLabel}>{textLabel}</label>
        <textarea
          className={styles.textInput}
          value={textValue}
          onChange={e => onTextChange(e.target.value)}
          placeholder={textPlaceholder}
          rows={4}
        />
      </div>
    ) : null}
  </>
);

export default Glp1MedicalScreening;
